import React from "react";
import t from "../../../../i18n";
import ModalForm from "@ui/Modal/Form";
import type { Project } from "../../../../api/useProjects";
import { styled } from "@linaria/react";
import Text, { H4 } from "@ui/Text";
import { InformationLine } from "@ui/IconV2/react";
import { color } from "@ui";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import useActivationFilters from "../../../../api/useActivationFilters";
import Button from "@ui/Button";
import { useModalSettingManagement } from "../../Settings";
import { useNavigate } from "react-router";
import { css } from "@linaria/core";
import Switch from "@ui/Switch";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import InputV2 from "@ui/Input/InputV2";
import { useProjectSource } from "../../../../api/useSources";
import { useAnalysisFrequencyInput } from "../../Settings/modalForm/AnalysisFrequencyModalForm";
import useInput from "@ui/lib/hooks/useInput";
import { usePatch, usePost } from "../../../../api/useCall";
import useActiveAnalysesStream from "../../../../api/useActiveAnalysesStream";
import { DateTime } from "luxon";
import classNames from "classnames";
import useToasts from "@ui/Toast/useToasts";
import { MAX_ANALYSIS_COUNT } from "diagnostics-domain/constants";
import { colors, fonts, spacings } from "@ui/Theme";
import { DatePicker } from "@ui/DatePicker";
import { parseDate, type DateValue } from "@internationalized/date";
import { toPath } from "../../../../router/helpers/path";

/**
 * Override default style
 */
const Section = styled.div`
  display: flex;
  flex-direction: column;
`;
const Title = styled(H4)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${color.carbon800};
  font-size: 16px;

  > span {
    display: inline-flex;
    color: ${color.carbon800};
  }
`;
const FiltersContainer = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;
const WidthConstraint = styled.div`
  width: 306px;
`;
const BlockSwitch = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${spacings.xs};
`;
const Period = styled.div`
  display: flex;
  column-gap: 8px;
`;
const StyledInputV2 = styled(InputV2)`
  width: 100%;
`;

/**
 * CSS
 */
const maginTop4pxCss = css`
  margin-top: 4px;
`;
const maginTop8pxCss = css`
  margin-top: 8px;
`;
const widthInput368Css = css`
  width: 368px;
`;
const timeCriteriaText = css`
  color: ${color.carbon800};
  margin-bottom: ${spacings.m};
  font: ${fonts.body1SemiBold};
  font-size: 16px;
`;
const dateRangeText = css`
  color: ${color.carbon700};
  margin-top: ${spacings.xxs};
  margin-bottom: ${spacings.m};
`;
const estimatedAnalysesText = css`
  margin-top: ${spacings.m};
`;
const estimatedAnalysessTimeCriteriaText = css`
  color: ${color.carbon700};
`;
const filtersLengthText = css`
  font: ${fonts.body1SemiBold};
`;
const informationLine = css`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.carbon800};
`;
const activationFiltersTitle = css`
  margin-bottom: ${spacings.xs};
  color: ${colors.carbon800};
  font-size: 16px;
`;

const modalContent = css`
  row-gap: 24px;
`;

/**
 * Constants
 */
type AnalysisModalType = "LAUNCH_ANALYSIS_SELECTION" | "RESTART_ANALYSIS_SELECTION";

/**
 * Typing */
interface P {
  project: Project;
  modalType: AnalysisModalType;
  onRequestClose: () => void;
}

const LaunchAnalysisSelectionModalForm = ({ modalType, project, onRequestClose }: P) => {
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const source = useProjectSource(project);
  if (source === null) throw new Error("Source is null");
  const { activeAnalyses } = useActiveAnalysesStream(project.id);
  const activationfilters = useActivationFilters(project.id);
  const [_, setSettingModal] = useModalSettingManagement({ scope: project.id });

  const dataFlowActivation = usePost("/projects/:projectId(uuid)/analyses/activationFlow");
  const resetActivation = usePost("/projects/:projectId(uuid)/analyses/activation/reset");
  const patchProject = usePatch("/projects");

  const {
    inputDays,
    inputHours,
    inputMinutes,
    errorInputDays,
    errorInputHours,
    errorInputMinutes,
    dirtyMinimumInterval: intervalInputDirty,
    notUseMinimumInterval: noIntervalInput,
    setNotUseMinimumInterval: setNoIntervalInput,
    minutesBetweenActiveAnalysis,
    ONE_YEAR_IN_MINUTE,
  } = useAnalysisFrequencyInput(project.minutesBetweenActiveAnalysis);

  // Date range
  const startSourceDate = source.minSnapshotDate;
  const endSourceDate = source.maxSnapshotDate;
  if (startSourceDate === null || endSourceDate === null)
    throw new Error("source is empty, no snapshot found");
  const startDateString = startSourceDate.split("T")[0] as string;
  const endDateString = endSourceDate.split("T")[0] as string;

  const [useDateInputs, setUseDateInputs] = React.useState(false);

  const {
    defaultValue: startDateDefaultValue,
    value: startDateValue,
    onChange: handleStartDateValueChanged,
    dirty: isStartedDateDirty,
  } = useInput(startDateString);
  const {
    defaultValue: endDateDefaultValue,
    value: endDateValue,
    onChange: handleEndDateValueChanged,
    dirty: isEndedDateDirty,
  } = useInput(endDateString);
  const intervalMinutes = noIntervalInput
    ? 0
    : Number.isNaN(minutesBetweenActiveAnalysis)
      ? null
      : minutesBetweenActiveAnalysis;

  const handleStartDateValueChangedAdapter = React.useCallback(
    (value: DateValue | null) => {
      handleStartDateValueChanged({
        currentTarget: { value: value ? value.toString() : "" },
      } as React.FormEvent<HTMLInputElement>);
    },
    [handleStartDateValueChanged],
  );

  const handleEndDateValueChangedAdapter = React.useCallback(
    (value: DateValue | null) => {
      handleEndDateValueChanged({
        currentTarget: { value: value ? value.toString() : "" },
      } as React.FormEvent<HTMLInputElement>);
    },
    [handleEndDateValueChanged],
  );

  const start = React.useMemo(() => {
    if (!startDateValue) return null;
    return useDateInputs
      ? DateTime.fromJSDate(new Date(startDateValue), { zone: project.timezone })
          .startOf("day")
          .toJSDate()
      : new Date(startSourceDate);
  }, [useDateInputs, project.timezone, startDateValue, startSourceDate]);

  const end = React.useMemo(() => {
    if (!endDateValue) return null;
    return useDateInputs
      ? DateTime.fromJSDate(new Date(endDateValue), { zone: project.timezone })
          .endOf("day")
          .toJSDate()
      : new Date(endSourceDate);
  }, [endDateValue, endSourceDate, useDateInputs, project.timezone]);

  const estimatedAnalyses = React.useMemo(() => {
    if (!start || !end || start.getTime() > end.getTime() || intervalMinutes === null) return null;
    if (intervalMinutes === 0) return source.snapshotCount;

    return Math.min(
      source.snapshotCount,
      Math.floor((end.getTime() - start.getTime()) / (intervalMinutes * 60 * 1000)),
    );
  }, [start, end, intervalMinutes, source.snapshotCount]);

  const errorStartedDate = React.useMemo(() => {
    if (!startDateValue || !startDateDefaultValue)
      return t(
        "project.modal_form.launch_analysis_selection.input.start_date.error.cannot_be_empty",
      );
    if (new Date(startDateValue).getTime() < new Date(startDateDefaultValue).getTime())
      return t(
        "project.modal_form.launch_analysis_selection.input.start_date.error.date_should_be_greater_or_equal_to_source",
      );
    if (
      endDateDefaultValue &&
      endDateValue &&
      new Date(startDateValue).getTime() > new Date(endDateValue).getTime()
    )
      return t(
        "project.modal_form.launch_analysis_selection.input.start_date.error.date_should_be_inferior_to_end_date",
      );

    return null;
  }, [endDateDefaultValue, endDateValue, startDateDefaultValue, startDateValue]);

  const errorEndedDate = React.useMemo(() => {
    if (!endDateDefaultValue || !endDateValue)
      return t("project.modal_form.launch_analysis_selection.input.end_date.error.cannot_be_empty");
    if (new Date(endDateValue).getTime() > new Date(endDateDefaultValue).getTime())
      return t(
        "project.modal_form.launch_analysis_selection.input.end_date.error.date_should_be_inferior_or_equal_to_source",
      );
    if (
      startDateDefaultValue &&
      startDateValue &&
      new Date(endDateValue).getTime() < new Date(startDateValue).getTime()
    )
      return t(
        "project.modal_form.launch_analysis_selection.input.end_date.error.date_should_be_greater_to_start_date",
      );

    return null;
  }, [endDateDefaultValue, endDateValue, startDateDefaultValue, startDateValue]);

  // Data Flow
  const [isLiveDataFlowActive, setIsLiveDataFlowActive] = React.useState(false);

  // Project
  const projectName = useInput();

  // Error
  const hasErrorDateRange = useDateInputs ? errorStartedDate || errorEndedDate : false;
  const hasErrorMinimumInterval = noIntervalInput
    ? false
    : errorInputDays || errorInputHours || errorInputMinutes;
  const hasError = hasErrorDateRange || hasErrorMinimumInterval;

  // Dirty
  const dirtyMinimumInterval = noIntervalInput ? false : intervalInputDirty;

  const handleSubmit = React.useCallback(async () => {
    if (intervalMinutes === null) throw new Error("intervalMinutes is null");
    if (!start || !end) throw new Error("Start or end date is null");
    if (intervalMinutes > ONE_YEAR_IN_MINUTE) {
      addToast("error", t("projects.settings.analyses_frequency.modal.submit.error.intervalToBig"));
      return false;
    }

    addToast(
      "success",
      t("projects.settings.analyses_frequency.modal.submit.toast.success.started"),
    );

    await resetActivation({
      routeParams: { projectId: project.id },
      body: { intervalMinutes, timeRange: { start, end } },
    });

    if (isLiveDataFlowActive) {
      await patchProject({
        body: {
          ids: [project.id],
          minutesBetweenActiveAnalysis: noIntervalInput ? 0 : minutesBetweenActiveAnalysis,
        },
      });
      await dataFlowActivation({
        routeParams: { projectId: project.id },
        body: { activationStatus: true },
      });
    }

    addToast(
      "success",
      t("projects.settings.analyses_frequency.modal.submit.toast.success.completed"),
    );

    return true;
  }, [
    ONE_YEAR_IN_MINUTE,
    addToast,
    dataFlowActivation,
    end,
    intervalMinutes,
    isLiveDataFlowActive,
    minutesBetweenActiveAnalysis,
    noIntervalInput,
    patchProject,
    project.id,
    resetActivation,
    start,
  ]);

  const datePickerOptionsStart = React.useMemo(
    () => ({
      defaultValue: startDateValue ? parseDate(startDateValue) : null,
      value: startDateValue ? parseDate(startDateValue) : null,
      placeholderValue: parseDate(startDateString),
      minValue: parseDate(startDateString),
      maxValue: endDateValue ? parseDate(endDateValue) : parseDate(endDateString),
      isDateUnavailable: (date: DateValue) =>
        date.compare(parseDate(startDateString)) < 0 ||
        (endDateValue
          ? date.compare(parseDate(endDateValue)) > 0
          : date.compare(parseDate(endDateString)) > 0),
      onChange: handleStartDateValueChangedAdapter,
    }),
    [
      startDateValue,
      startDateString,
      endDateValue,
      endDateString,
      handleStartDateValueChangedAdapter,
    ],
  );

  const datePickerOptionsEnd = React.useMemo(
    () => ({
      defaultValue: endDateValue ? parseDate(endDateValue) : null,
      value: endDateValue ? parseDate(endDateValue) : null,
      placeholderValue: parseDate(endDateString),
      minValue: startDateValue ? parseDate(startDateValue) : parseDate(startDateString),
      maxValue: parseDate(endDateString),
      isDateUnavailable: (date: DateValue) =>
        (startDateValue
          ? date.compare(parseDate(startDateValue)) < 0
          : date.compare(parseDate(startDateString)) < 0) ||
        date.compare(parseDate(endDateString)) > 0,
      onChange: handleEndDateValueChangedAdapter,
    }),
    [
      startDateValue,
      endDateValue,
      startDateString,
      endDateString,
      handleEndDateValueChangedAdapter,
    ],
  );

  return (
    <ModalForm
      className={modalContent}
      onSubmit={
        estimatedAnalyses !== null &&
        estimatedAnalyses > 0 &&
        !hasError &&
        (modalType === "LAUNCH_ANALYSIS_SELECTION" ||
          (modalType === "RESTART_ANALYSIS_SELECTION" && project.name === projectName.value))
          ? handleSubmit
          : false
      }
      onRequestClose={onRequestClose}
      okButtonName={
        modalType === "LAUNCH_ANALYSIS_SELECTION"
          ? t("project.modal_form.ok_button_name.launch_analysis_selection")
          : t("project.modal_form.ok_button_name.restart_analysis_selection")
      }
    >
      <>
        <Section>
          <Text className={timeCriteriaText}>{t("project.modal_form.text.time_criteria")}</Text>
          <BlockSwitch>
            <Switch checked={useDateInputs} onChange={() => setUseDateInputs(s => !s)}>
              {t("project.modal_form.title.date_range")}
            </Switch>
            <TooltipV2
              placement="right"
              overlay={
                <WidthConstraint>
                  <OverlayWrapper text={t("project.modal_form.title.overlay.date_range")} />
                </WidthConstraint>
              }
            >
              <span className={informationLine}>
                <InformationLine />
              </span>
            </TooltipV2>
          </BlockSwitch>
          <div className={maginTop8pxCss}>
            <Period>
              <DatePicker
                id="date_start"
                data-cy="date-range-start"
                dateOptions={datePickerOptionsStart}
                revertable={startDateValue !== startDateDefaultValue}
                revert={() => handleStartDateValueChangedAdapter(parseDate(startDateString))}
                error={
                  useDateInputs ? (isStartedDateDirty ? errorStartedDate : undefined) : undefined
                }
                label={t("project.modal_form.date_range.input.date_start")}
                disabled={!useDateInputs}
              />
              <DatePicker
                id="date_end"
                data-cy="date-range-end"
                dateOptions={datePickerOptionsEnd}
                revertable={endDateValue !== endDateDefaultValue}
                revert={() => handleEndDateValueChangedAdapter(parseDate(endDateString))}
                error={useDateInputs ? (isEndedDateDirty ? errorEndedDate : undefined) : undefined}
                label={t("project.modal_form.date_range.input.date_end")}
                disabled={!useDateInputs}
              />
            </Period>
          </div>
          <Text type="body2" className={dateRangeText}>
            {t("project.modal_form.text.date_range")}
          </Text>
          <BlockSwitch>
            <Switch checked={!noIntervalInput} onChange={() => setNoIntervalInput(v => !v)}>
              {t("project.modal_form.title.minimum_interval")}
            </Switch>
            <TooltipV2
              placement="right"
              overlay={
                <WidthConstraint>
                  <OverlayWrapper text={t("project.modal_form.title.overlay.minimum_interval")} />
                </WidthConstraint>
              }
            >
              <span className={informationLine}>
                <InformationLine />
              </span>
            </TooltipV2>
          </BlockSwitch>
          <div className={maginTop8pxCss}>
            <Period>
              <StyledInputV2
                label={t("projects.settings.analyses_frequency.modal.edit.input.days")}
                data-cy="analysis-frequency-days"
                type="number"
                step={1}
                max={365}
                min={0}
                error={
                  noIntervalInput ? undefined : dirtyMinimumInterval ? errorInputDays : undefined
                }
                {...inputDays}
                disabled={noIntervalInput}
                revertable={false}
              />
              <StyledInputV2
                label={t("projects.settings.analyses_frequency.modal.edit.input.hours")}
                data-cy="analysis-frequency-hours"
                type="number"
                step={1}
                max={365 * 24}
                min={0}
                error={
                  noIntervalInput ? undefined : dirtyMinimumInterval ? errorInputHours : undefined
                }
                {...inputHours}
                disabled={noIntervalInput}
                revertable={false}
              />
              <StyledInputV2
                label={t("projects.settings.analyses_frequency.modal.edit.input.minutes")}
                data-cy="analysis-frequency-minutes"
                type="number"
                step={1}
                max={365 * 24 * 60}
                min={0}
                error={
                  noIntervalInput ? undefined : dirtyMinimumInterval ? errorInputMinutes : undefined
                }
                {...inputMinutes}
                disabled={noIntervalInput}
                revertable={false}
              />
            </Period>
          </div>

          {estimatedAnalyses === null ? null : (
            <div>
              <>
                <Text className={estimatedAnalysesText}>
                  {t(
                    "project.modal_form.estimated_analyses.text",
                    estimatedAnalyses,
                    activeAnalyses.length,
                  )}
                </Text>
                <Text type="body2" className={estimatedAnalysessTimeCriteriaText}>
                  {t("project.modal_form.estimated_analyses.time_criteria.text")}
                </Text>
                {estimatedAnalyses > MAX_ANALYSIS_COUNT ? (
                  <AdditionnalMessage
                    className={maginTop4pxCss}
                    status="WARNING"
                    text={t("project.modal_form.additionnal_message.max_analyses_count")}
                  />
                ) : (
                  ""
                )}
              </>
            </div>
          )}
        </Section>
        <Section>
          <Title className={activationFiltersTitle}>
            {t("project.modal_form.title.activation_filters")}
            <TooltipV2
              placement="right"
              overlay={
                <WidthConstraint>
                  <OverlayWrapper text={t("project.modal_form.title.overlay.activation_filters")} />
                </WidthConstraint>
              }
            >
              <span className={informationLine}>
                <InformationLine />
              </span>
            </TooltipV2>
          </Title>
          <FiltersContainer>
            {activationfilters.length > 0 ? (
              <>
                <Text className={filtersLengthText}>{activationfilters.length}</Text>
                <Button
                  variant="link"
                  type="button"
                  onClick={() => {
                    onRequestClose();
                    void navigate(
                      toPath("/projects/:projectId/settings/filters", {
                        projectId: project.id,
                      }),
                    );
                  }}
                >
                  {t(
                    "project.modal_form.button.activation_filters.with_filter",
                    activationfilters.length,
                  )}
                </Button>
              </>
            ) : (
              <>
                <Text>{t("project.modal_form.text.activation_filters.without_filter")}</Text>
                <Button
                  variant="link"
                  type="button"
                  onClick={() => {
                    onRequestClose();
                    setSettingModal("analysesFiltersLoad");
                    void navigate(
                      toPath("/projects/:projectId/settings", {
                        projectId: project.id,
                      }),
                    );
                  }}
                >
                  {t("project.modal_form.button.activation_filters.without_filter")}
                </Button>
              </>
            )}
          </FiltersContainer>
        </Section>

        {modalType === "LAUNCH_ANALYSIS_SELECTION" ? (
          <Section>
            <BlockSwitch>
              <Title>
                {t("project.modal_form.title.live_data_stream")}
                <TooltipV2
                  placement="right"
                  overlay={
                    <WidthConstraint>
                      <OverlayWrapper
                        text={t("project.modal_form.title.overlay.live_data_stream")}
                      />
                    </WidthConstraint>
                  }
                >
                  <span className={informationLine}>
                    <InformationLine />
                  </span>
                </TooltipV2>
              </Title>
            </BlockSwitch>
            <div className={maginTop8pxCss}>
              <Switch
                checked={isLiveDataFlowActive}
                onChange={() => setIsLiveDataFlowActive(s => !s)}
              >
                {t("project.modal_form.switch.switch.activate_live_stream")}
              </Switch>
            </div>
          </Section>
        ) : (
          <Section>
            <Title>{t("project.modal_form.title.action_confirmation")}</Title>
            <div className={maginTop8pxCss}>
              <Text>{t("project.modal_form.action_confirmation.description", project.name)}</Text>
              <InputV2
                required
                label={t("projects.modal.delete.input_placeholder")}
                placeholder={t("projects.modal.delete.input_placeholder")}
                {...projectName}
                className={classNames(widthInput368Css, maginTop8pxCss)}
              />
              <AdditionnalMessage
                className={maginTop8pxCss}
                status="WARNING"
                text={t(
                  "project.modal_form.restart_analysis_selection.additionnal_message.warningt.loss_of_action",
                )}
              />
            </div>
          </Section>
        )}
      </>
    </ModalForm>
  );
};
export default React.memo(LaunchAnalysisSelectionModalForm);
